import React from 'react';
import { FormattedMessage } from 'react-intl';
import Badge, { BadgeWrapper } from '~/components/atoms/badge/badge';
import Badges from '~/components/molecules/badges/badges';
import CancelAllFiltersButton from '~/components/organisms/filters/cancelAllFiltersButton/CancelAllFiltersButton';
import CancelFilterButton from '~/components/organisms/filters/cancelFilterButton/CancelFilterButton';
import typography from '~/components/styles/typography.module.scss';
import { BooleanFilter_ControlsState, BooleanFilter_State } from '~/core/features/filtering/types/booleanFilter.type';
import {
    Filter_ControlsState,
    Filter_State,
    Filters_ControlsState,
    Filters_State,
} from '~/core/features/filtering/types/filters.type';
import { KeyWordFilter_ControlsState, KeyWordFilter_State } from '~/core/features/filtering/types/keywordFilter.type';
import { NumberFilter_ControlsState, NumberFilter_State } from '~/core/features/filtering/types/numberFilter.type';
import { CancelAllFilters, CancelFilter } from '~/core/useSearchState';
import { numberFormatter } from '~/utils/numberFormatter';

type AppliedFiltersProps = {
    filtersControls: Filters_ControlsState;
    filtersState: Filters_State;
    cancelFilter: CancelFilter;
    cancelAllFilters: CancelAllFilters;
    querySetIdentifier: string;
};

type AppliedFilterProps = {
    querySetIdentifier: string;
    cancelFilter: CancelFilter;
    filterControls: Filter_ControlsState;
    filterState: Filter_State;
};

const KeyWordAppliedFilter: React.FC<
    AppliedFilterProps & { filterControls: KeyWordFilter_ControlsState; filterState: KeyWordFilter_State }
> = ({ querySetIdentifier, filterControls, cancelFilter }) => {
    return (
        <>
            {filterControls.values.keywords
                .filter((value) => value.isChecked)
                .map((value, index) => {
                    return (
                        <li key={`${filterControls.identifier}-${index}`}>
                            <BadgeWrapper key={index} data-testid="filters-badge">
                                <Badge text={`${value.key}`} key={value.key} data-testid="filters-badge-name" />
                                <CancelFilterButton
                                    onClick={() =>
                                        cancelFilter({
                                            querySetIdentifier,
                                            filterIdentifier: filterControls.identifier,
                                            key: value.key,
                                        })
                                    }
                                    data-testid="filters-badge-close"
                                />
                            </BadgeWrapper>
                        </li>
                    );
                })}
        </>
    );
};

const NumberAppliedFilter: React.FC<
    AppliedFilterProps & { filterControls: NumberFilter_ControlsState; filterState: NumberFilter_State }
> = ({ querySetIdentifier, filterControls, filterState, cancelFilter }) => {
    return (
        <>
            <BadgeWrapper data-testid="filters-badge">
                <Badge
                    text={
                        <span>
                            {`${filterState.name}: `}
                            <FormattedMessage
                                id="badge_from_to_label"
                                defaultMessage="Od {from} do {to} {currency}"
                                values={{
                                    from: `${numberFormatter(filterControls.values.min)}`,
                                    to: `${numberFormatter(filterControls.values.max)}`,
                                    currency: filterState.configuration.unit ?? '',
                                }}
                            />
                        </span>
                    }
                    data-testid="filters-badge-name"
                />
                <CancelFilterButton
                    onClick={() =>
                        cancelFilter({
                            querySetIdentifier,
                            filterIdentifier: filterState.identifier,
                        })
                    }
                    data-testid="filters-badge-close"
                />
            </BadgeWrapper>
        </>
    );
};

const BooleanAppliedFilter: React.FC<
    AppliedFilterProps & { filterControls: BooleanFilter_ControlsState; filterState: BooleanFilter_State }
> = ({ querySetIdentifier, filterState, cancelFilter }) => {
    return (
        <BadgeWrapper data-testid="filters-badge">
            <Badge text={`${filterState.configuration.checkboxDescription}`} data-testid="filters-badge-name" />
            <CancelFilterButton
                onClick={() =>
                    cancelFilter({
                        querySetIdentifier,
                        filterIdentifier: filterState.identifier,
                    })
                }
                data-testid="filters-badge-close"
            />
        </BadgeWrapper>
    );
};

const AppliedFilters: React.FC<AppliedFiltersProps> = ({
    filtersState,
    filtersControls,
    cancelFilter,
    cancelAllFilters,
    querySetIdentifier,
}) => {
    return (
        <Badges
            header={
                <div role="heading" aria-level={3} className={typography.serpUppercaseTitle}>
                    <FormattedMessage id="badges_title" defaultMessage="Vybrali jste" />
                </div>
            }
        >
            <ul>
                {filtersControls
                    .filter((filterControls) => filterControls.applyFilter)
                    .map((filterControls) => {
                        const filterState: Filter_State | undefined = filtersState.find(
                            (filterState) => filterState.identifier === filterControls.identifier
                        );

                        if (!filterState) {
                            throw new Error(`Filter state for ${filterControls.identifier} not found`);
                        }

                        if (filterState.type === 'keyword' && filterControls.type === 'keyword') {
                            return (
                                <KeyWordAppliedFilter
                                    filterControls={filterControls}
                                    filterState={filterState}
                                    cancelFilter={cancelFilter}
                                    querySetIdentifier={querySetIdentifier}
                                    key={filterState.identifier}
                                />
                            );
                        }

                        if (
                            (filterState.type === 'float' || filterState.type === 'integer') &&
                            (filterControls.type === 'float' || filterControls.type === 'integer')
                        ) {
                            return (
                                <li key={filterState.identifier}>
                                    <NumberAppliedFilter
                                        filterState={filterState}
                                        filterControls={filterControls}
                                        cancelFilter={cancelFilter}
                                        querySetIdentifier={querySetIdentifier}
                                    />
                                </li>
                            );
                        }

                        if (filterState.type === 'boolean' && filterControls.type === 'boolean') {
                            return (
                                <li key={filterState.identifier}>
                                    <BooleanAppliedFilter
                                        filterState={filterState}
                                        filterControls={filterControls}
                                        cancelFilter={cancelFilter}
                                        querySetIdentifier={querySetIdentifier}
                                    />
                                </li>
                            );
                        }
                    })}
                <li key={'cancel-badge'}>
                    <CancelAllFiltersButton
                        onClick={() => cancelAllFilters({ querySetIdentifier: querySetIdentifier })}
                    />
                </li>
            </ul>
        </Badges>
    );
};

export default AppliedFilters;
